import { useEffect, useRef } from 'react';

/**
 * Tiny keyboard-shortcut runtime. Two patterns:
 *
 *   useShortcut('mod+s', save)
 *   useShortcut('mod+shift+z', redo)
 *
 *   useChord(['g', 'd'], () => navigate('/app'))
 *
 * `mod` resolves to ⌘ on macOS and Ctrl elsewhere — matches what users see
 * in product UI. Combos are case-insensitive on the key portion.
 *
 * Both helpers are no-ops when focus is in an `<input>`/`<textarea>`/
 * contenteditable, so power-user nav doesn't fight typing. Pass
 * `{ allowInInput: true }` to override (e.g. Cmd+S inside the editor — saving
 * while typing is fine).
 */

type Combo = {
  mod: boolean;
  shift: boolean;
  alt: boolean;
  key: string; // lowercase
};

function parse(spec: string): Combo {
  const parts = spec.toLowerCase().split('+').map((p) => p.trim());
  const combo: Combo = { mod: false, shift: false, alt: false, key: '' };
  for (const p of parts) {
    if (p === 'mod' || p === 'cmd' || p === 'ctrl') combo.mod = true;
    else if (p === 'shift') combo.shift = true;
    else if (p === 'alt' || p === 'option') combo.alt = true;
    else combo.key = p;
  }
  return combo;
}

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
}

function matches(e: KeyboardEvent, combo: Combo): boolean {
  const modPressed = isMac() ? e.metaKey : e.ctrlKey;
  if (combo.mod !== modPressed) return false;
  if (combo.shift !== e.shiftKey) return false;
  if (combo.alt !== e.altKey) return false;
  return e.key.toLowerCase() === combo.key;
}

function targetIsInput(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useShortcut(
  spec: string,
  handler: (e: KeyboardEvent) => void,
  opts: { allowInInput?: boolean; deps?: unknown[] } = {},
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  // We intentionally don't include `handler` in deps — keeping the listener
  // identity stable while reading the latest handler via ref.
   
  useEffect(() => {
    const combo = parse(spec);
    const onKey = (e: KeyboardEvent) => {
      if (!matches(e, combo)) return;
      if (!opts.allowInInput && targetIsInput(e)) return;
      e.preventDefault();
      handlerRef.current(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [spec, opts.allowInInput, ...(opts.deps ?? [])]);
}

/**
 * Vim-style chord: press the leader key, then the second key within
 * `withinMs`. Resets if the user types anything else or the timer expires.
 * Skipped when focus is in an input, regardless — chords would be
 * unusable while typing.
 */
export function useChord(
  keys: [string, string],
  handler: () => void,
  opts: { withinMs?: number } = {},
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    let armed = false;
    let timer: number | null = null;
    const reset = () => {
      armed = false;
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (targetIsInput(e)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (!armed && k === keys[0].toLowerCase()) {
        armed = true;
        timer = window.setTimeout(reset, opts.withinMs ?? 1000);
        return;
      }
      if (armed) {
        if (k === keys[1].toLowerCase()) {
          reset();
          e.preventDefault();
          handlerRef.current();
        } else {
          reset();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      reset();
    };
  }, [keys[0], keys[1], opts.withinMs]);
}
